import React, { useEffect, useState, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, ReferenceLine } from 'recharts';
import { Download, X } from 'lucide-react';
import { api } from '../lib/api';
import { MuteButton } from '../components/NotificationProvider';
import { useBranchStore } from '../store/branchStore';
import { useBranchChangeListener } from '../lib/branchChangeEvent';

interface ReportsMetrics {
  responseTime: Array<{ month: string; value: number | null; dataPoints?: number }>;
  runningTime: Array<{ month: string; value: number | null; dataPoints?: number }>;
  supplyInterruption: Array<{ month: string; value: number | null; dataPoints?: number }>;
  totalProcessing: Array<{ month: string; value: number | null; dataPoints?: number }>;
  realLosses: Array<{ month: string; value: number | null }>;
  statusDistribution: Array<{ status: string; count: number; percentage: number }>;
  pipeDiameterDistribution: Array<{ bin: string; count: number; percentage: number }>;
  pipeMaterialDistribution: Array<{ category: string; count: number; percentage: number }>;
  averages?: {
    responseTime: number | null;
    runningTime: number | null;
    supplyInterruption: number | null;
    totalProcessing: number | null;
    realLosses: number | null;
  };
}

const COLORS_DISTRIBUTION = ['#3b82f6', '#f59e0b', '#fb923c', '#10b981'];
const CHART_HEIGHT = 350;

// Smart time formatting: minutes -> hours -> days
function formatDuration(hours: number | null | undefined): string {
  if (hours === null || hours === undefined) return 'N/A';

  const minutes = hours * 60;

  // Less than 60 minutes - show minutes only
  if (minutes < 60) {
    return `${Math.round(minutes)}min`;
  }

  // Less than 1440 minutes (24 hours) - show hours and minutes
  if (minutes < 1440) {
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    return m === 0 ? `${h}h` : `${h}h ${m}min`;
  }

  // 24 hours or more - show only days (simplified for averages)
  const days = Math.round(minutes / 1440);
  return `${days}d`;
}

// Format Y-axis labels for time-based charts
function formatYAxisTime(value: number): string {
  if (value === 0) return '0';
  return formatDuration(value);
}

// Custom legend renderer to include average line
function CustomLegend(props: any) {
  const { payload } = props;
  if (!payload) return null;

  return (
    <div className="flex flex-wrap gap-6 mt-4 justify-center">
      {payload.map((entry: any, index: number) => (
        <div key={`legend-${index}`} className="flex items-center gap-2">
          {entry.type === 'line' ? (
            <div
              className="w-6 h-1"
              style={{
                backgroundColor: entry.color,
                borderBottom: `3px dashed ${entry.color}`,
              }}
            />
          ) : (
            <div
              className="w-3 h-3"
              style={{ backgroundColor: entry.color }}
            />
          )}
          <span className="text-sm text-gray-700">{entry.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function Reports() {
  const [metrics, setMetrics] = useState<ReportsMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  // Time filter state
  const currentYear = new Date().getFullYear();
  const [timeRange, setTimeRange] = useState<'1m' | '3m' | '6m' | '12m' | null>('1m');
  const [selectedYear, setSelectedYear] = useState<number | null>(null); // null = current year, or a past year

  const activeBranch = useBranchStore((state) => state.activeBranch);
  const activeBranchId = activeBranch?.id;

  // Listen for branch change events
  useBranchChangeListener(
    useCallback((branch) => {
      setLoading(true);
      fetchMetrics(branch.id);
    }, [timeRange, selectedYear])
  );

  // Initial load and on filter change
  useEffect(() => {
    if (activeBranchId) {
      setLoading(true);
      fetchMetrics(activeBranchId);
    }
  }, [activeBranchId, timeRange, selectedYear]);

  const calculateDateRange = () => {
    // If a historical year is selected, fetch full year data with no date filters
    if (selectedYear !== null && selectedYear < currentYear) {
      return {
        startDate: undefined,
        endDate: undefined,
        year: selectedYear,
      };
    }

    // Current year with time range filters
    const now = new Date();
    const end = new Date(currentYear, now.getMonth(), now.getDate()); // Today in current year

    const monthsBack = timeRange === '1m' ? 1 : timeRange === '3m' ? 3 : timeRange === '6m' ? 6 : 12;
    const start = new Date(end);
    start.setMonth(start.getMonth() - monthsBack + 1);
    start.setDate(1); // First day of the month

    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
      year: undefined,
    };
  };

  const fetchMetrics = async (branchId: string) => {
    try {
      const dateRange = calculateDateRange();
      const params = new URLSearchParams();
      params.append('branchId', branchId);

      if (dateRange.startDate && dateRange.endDate) {
        params.append('startDate', dateRange.startDate);
        params.append('endDate', dateRange.endDate);
      } else if (dateRange.year) {
        params.append('year', dateRange.year.toString());
      }

      const data = await api.get<{ metrics: ReportsMetrics }>(
        `/reports/metrics?${params.toString()}`
      );
      setMetrics(data.metrics);
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setTimeRange('1m');
    setSelectedYear(null);
  };

  // Generate list of available years (5 past years from current year)
  const availableYears = Array.from({ length: 5 }, (_, i) => currentYear - 5 + i).reverse();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>
          <p className="text-gray-500 mt-1">Operational metrics and leak analysis</p>
        </div>
        <MuteButton />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 mb-6">
        <div className="space-y-4">
          {/* Current Year Time Range Section */}
          <div className="border-b pb-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Current Year</h3>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-700">Time Range:</span>
              <div className="flex gap-2">
                {(['1m', '3m', '6m', '12m'] as const).map((range) => (
                  <button
                    key={range}
                    onClick={() => {
                      setTimeRange(range);
                      setSelectedYear(null);
                    }}
                    disabled={selectedYear !== null}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedYear === null && timeRange === range
                        ? 'bg-cyan-600 text-white'
                        : selectedYear !== null
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {range === '1m' ? '1 Month' : range === '3m' ? '3 Months' : range === '6m' ? '6 Months' : '12 Months'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Historical Years Section */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Historical Years</h3>
            <div className="flex items-center gap-2 flex-wrap">
              {availableYears.map((year) => (
                <button
                  key={year}
                  onClick={() => {
                    setSelectedYear(year);
                    setTimeRange(null);
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedYear === year
                      ? 'bg-cyan-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {year}
                </button>
              ))}
            </div>
          </div>

          {/* Reset Filter Button */}
          {(timeRange !== '1m' || selectedYear !== null) && (
            <div className="pt-2 border-t">
              <button
                onClick={resetFilters}
                className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-4 w-4" />
                Reset Filter
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Charts Grid */}
      {!metrics || (
        !metrics.statusDistribution?.length &&
        !metrics.responseTime?.length &&
        !metrics.runningTime?.length &&
        !metrics.supplyInterruption?.length &&
        !metrics.totalProcessing?.length &&
        !metrics.realLosses?.length &&
        !metrics.pipeDiameterDistribution?.length &&
        !metrics.pipeMaterialDistribution?.length
      ) ? (
        <div className="bg-white rounded-xl shadow-sm p-12 border border-gray-200 text-center">
          <p className="text-gray-500 text-lg">No data available for the selected filters</p>
        </div>
      ) : (
        <div className="space-y-6">
        {/* Status Distribution */}
        {metrics?.statusDistribution && metrics.statusDistribution.length > 0 && selectedYear === null && (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Request Status Distribution</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={metrics.statusDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ status, percentage }) => `${status}: ${percentage.toFixed(1)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {metrics.statusDistribution.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS_DISTRIBUTION[index % COLORS_DISTRIBUTION.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: any) => `${value} requests`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* 1. Response Time Chart */}
        {metrics?.responseTime && metrics.responseTime.length > 0 && (() => {
          const filteredData = metrics.responseTime.filter((d) => d.value !== null);
          if (filteredData.length === 0) return null;

          const avg = metrics.averages?.responseTime ?? null;

          const legendData = [
            { value: 'Response Time', color: '#06b6d4', type: 'bar' },
            ...(avg !== null && avg !== undefined ? [{ value: `Avg. Time ${formatDuration(avg)}`, color: '#06b6d4', type: 'line' }] : []),
          ];

          return (
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Response Time</h2>
              <p className="text-sm text-gray-600 mb-4">Report → On Site</p>
              <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                <BarChart data={filteredData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis type="number" tickFormatter={formatYAxisTime} />
                  <Tooltip
                    formatter={(value: any) => {
                      if (value === null) return 'N/A';
                      return formatDuration(value);
                    }}
                    labelFormatter={(label) => `Month: ${label}`}
                  />
                  <Bar dataKey="value" fill="#06b6d4" name="Response Time" />
                  {avg !== null && avg !== undefined && (
                    <ReferenceLine
                      y={avg}
                      stroke="#06b6d4"
                      strokeWidth={2}
                      strokeDasharray="7 7"
                    />
                  )}
                </BarChart>
              </ResponsiveContainer>
              <CustomLegend payload={legendData} />
            </div>
          );
        })()}

        {/* 2. Running Time Chart */}
        {metrics?.runningTime && metrics.runningTime.length > 0 && (() => {
          const filteredData = metrics.runningTime.filter((d) => d.value !== null);
          if (filteredData.length === 0) return null;

          const avg = metrics.averages?.runningTime ?? null;

          const legendData = [
            { value: 'Running Time', color: '#f59e0b', type: 'bar' },
            ...(avg !== null && avg !== undefined ? [{ value: `Avg. Time ${formatDuration(avg)}`, color: '#f59e0b', type: 'line' }] : []),
          ];

          return (
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Leak Running Time</h2>
              <p className="text-sm text-gray-600 mb-4">Report → Decommissioning</p>
              <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                <BarChart data={filteredData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis type="number" tickFormatter={formatYAxisTime} />
                  <Tooltip
                    formatter={(value: any) => {
                      if (value === null) return 'N/A';
                      return formatDuration(value);
                    }}
                    labelFormatter={(label) => `Month: ${label}`}
                  />
                  <Bar dataKey="value" fill="#f59e0b" name="Running Time" />
                  {avg !== null && avg !== undefined && (
                    <ReferenceLine
                      y={avg}
                      stroke="#f59e0b"
                      strokeWidth={2}
                      strokeDasharray="7 7"
                    />
                  )}
                </BarChart>
              </ResponsiveContainer>
              <CustomLegend payload={legendData} />
            </div>
          );
        })()}

        {/* 3. Supply Interruption Time Chart */}
        {metrics?.supplyInterruption && metrics.supplyInterruption.length > 0 && (() => {
          const filteredData = metrics.supplyInterruption.filter((d) => d.value !== null);
          if (filteredData.length === 0) return null;

          const avg = metrics.averages?.supplyInterruption ?? null;

          const legendData = [
            { value: 'Supply Interruption Time', color: '#a78bfa', type: 'bar' },
            ...(avg !== null && avg !== undefined ? [{ value: `Avg. Time ${formatDuration(avg)}`, color: '#a78bfa', type: 'line' }] : []),
          ];

          return (
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Supply Interruption Time</h2>
              <p className="text-sm text-gray-600 mb-4">Decommissioning → Recommissioning</p>
              <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                <BarChart data={filteredData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis type="number" tickFormatter={formatYAxisTime} />
                  <Tooltip
                    formatter={(value: any) => {
                      if (value === null) return 'N/A';
                      return formatDuration(value);
                    }}
                    labelFormatter={(label) => `Month: ${label}`}
                  />
                  <Bar dataKey="value" fill="#a78bfa" name="Supply Interruption Time" />
                  {avg !== null && avg !== undefined && (
                    <ReferenceLine
                      y={avg}
                      stroke="#a78bfa"
                      strokeWidth={2}
                      strokeDasharray="7 7"
                    />
                  )}
                </BarChart>
              </ResponsiveContainer>
              <CustomLegend payload={legendData} />
            </div>
          );
        })()}

        {/* 4. Total Processing Time Chart */}
        {metrics?.totalProcessing && metrics.totalProcessing.length > 0 && (() => {
          const filteredData = metrics.totalProcessing.filter((d) => d.value !== null);
          if (filteredData.length === 0) return null;

          const avg = metrics.averages?.totalProcessing ?? null;

          const legendData = [
            { value: 'Total Processing Time', color: '#10b981', type: 'bar' },
            ...(avg !== null && avg !== undefined ? [{ value: `Avg. Time ${formatDuration(avg)}`, color: '#10b981', type: 'line' }] : []),
          ];

          return (
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Total Processing Time</h2>
              <p className="text-sm text-gray-600 mb-4">Report → Completed</p>
              <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                <BarChart data={filteredData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis type="number" tickFormatter={formatYAxisTime} />
                  <Tooltip
                    formatter={(value: any) => {
                      if (value === null) return 'N/A';
                      return formatDuration(value);
                    }}
                    labelFormatter={(label) => `Month: ${label}`}
                  />
                  <Bar dataKey="value" fill="#10b981" name="Total Processing Time" />
                  {avg !== null && avg !== undefined && (
                    <ReferenceLine
                      y={avg}
                      stroke="#10b981"
                      strokeWidth={2}
                      strokeDasharray="7 7"
                    />
                  )}
                </BarChart>
              </ResponsiveContainer>
              <CustomLegend payload={legendData} />
            </div>
          );
        })()}

        {/* 5. Real Losses Chart */}
        {metrics?.realLosses && metrics.realLosses.length > 0 && (() => {
          const filteredData = metrics.realLosses.filter((d) => d.value !== null);
          if (filteredData.length === 0) return null;

          const avg = metrics.averages?.realLosses ?? null;

          const legendData = [
            { value: 'Real Losses', color: '#ef4444', type: 'bar' },
            ...(avg !== null && avg !== undefined ? [{ value: `Avg. Loss ${avg.toFixed(2)}m³`, color: '#ef4444', type: 'line' }] : []),
          ];

          return (
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Real Losses by Pipe Damages</h2>
              <p className="text-sm text-gray-600 mb-4">Monthly total water losses (m³/month)</p>
              <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                <BarChart data={filteredData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis type="number" tickFormatter={(value) => `${value.toFixed(0)}m³`} />
                  <Tooltip
                    formatter={(value: any) => {
                      if (value === null) return 'N/A';
                      return `${value.toFixed(2)}m³`;
                    }}
                    labelFormatter={(label) => `Month: ${label}`}
                  />
                  <Bar dataKey="value" fill="#ef4444" name="Real Losses" />
                  {avg !== null && avg !== undefined && (
                    <ReferenceLine
                      y={avg}
                      stroke="#ef4444"
                      strokeWidth={2}
                      strokeDasharray="7 7"
                    />
                  )}
                </BarChart>
              </ResponsiveContainer>
              <CustomLegend payload={legendData} />
            </div>
          );
        })()}

        {/* 6. Pipe Diameter Distribution */}
        {metrics?.pipeDiameterDistribution && metrics.pipeDiameterDistribution.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Pipe Diameter Distribution</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={metrics.pipeDiameterDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ bin, percentage }) => `${bin}: ${percentage.toFixed(1)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {metrics.pipeDiameterDistribution.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS_DISTRIBUTION[index % COLORS_DISTRIBUTION.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: any) => `${value} requests`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* 7. Pipe Material Distribution */}
        {metrics?.pipeMaterialDistribution && metrics.pipeMaterialDistribution.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Pipe Material Distribution</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={metrics.pipeMaterialDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ category, percentage }) => `${category}: ${percentage.toFixed(1)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {metrics.pipeMaterialDistribution.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS_DISTRIBUTION[index % COLORS_DISTRIBUTION.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: any) => `${value} requests`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
